import { useState } from "react";
import { legalAPI } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const EMPTY = { first_name: "", last_name: "", company: "", phone: "", website: "", email: "", message: "" };

export default function ContactModal({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in the required fields");
      return;
    }
    setLoading(true);
    try {
      await legalAPI.submitContact(form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto border-[hsl(240_6%_17%)] bg-[hsl(240_8%_8%)] p-6 sm:max-w-lg"
        data-testid="contact-modal"
      >
        <DialogTitle className="sr-only">Contact us</DialogTitle>
        <div className="mb-2 flex flex-col items-center text-center">
          <Logo size="md" className="mb-3 flex-col gap-3 sm:flex-row" />
          <h1 className="font-display text-2xl font-bold tracking-wide">Get in touch</h1>
          <p className="mt-2 text-sm text-muted-foreground">Send us a message and we&apos;ll get back to you.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="c_first">First name *</Label>
              <Input id="c_first" value={form.first_name} onChange={upd("first_name")} required data-testid="contact-firstname" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c_last">Last name *</Label>
              <Input id="c_last" value={form.last_name} onChange={upd("last_name")} required data-testid="contact-lastname" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="c_email">Email *</Label>
            <Input id="c_email" type="email" value={form.email} onChange={upd("email")} required data-testid="contact-email" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="c_company">Company</Label>
              <Input id="c_company" value={form.company} onChange={upd("company")} data-testid="contact-company" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="c_phone">Phone</Label>
              <Input id="c_phone" value={form.phone} onChange={upd("phone")} data-testid="contact-phone" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="c_website">Website</Label>
            <Input id="c_website" value={form.website} onChange={upd("website")} data-testid="contact-website" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="c_message">Message *</Label>
            <Textarea id="c_message" rows={4} value={form.message} onChange={upd("message")} required data-testid="contact-message" />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)} data-testid="contact-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="contact-send">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
